import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Trash, Flask, ArrowRight, Warning, CheckCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* =========================================================================
 *  AssessorRunPanel — Inline 'Run Assessment' workspace for the assessor.
 *
 *  Two modes:
 *    • Scratchpad   — runs under the assessor's own admin account
 *                     (default). The result lives in their formulations.
 *    • On behalf of — assessor picks a client from the dropdown; the
 *                     formulation is attributed to that client_user_id and
 *                     tagged assessor_proxy=true / assessor_id=<admin>.
 *
 *  Covers all 6 ToxIntel workflows: hazard, tra_qra, full, prop65, sds,
 *  claims. Hits the existing POST /api/formulations engine (the assessor
 *  proxy field was added in this iteration).
 * ======================================================================= */

const MODES = [
  { id: 'hazard',  label: 'Risk Screen',        desc: 'Scientific Risk Memo (3-day triage)' },
  { id: 'tra_qra', label: 'TRA / QRA',          desc: 'Route-specific SED · NOAEL · MoS' },
  { id: 'full',    label: 'Full Assessment',    desc: 'EC 1223 Part A + Part B (Aᴳ-signed)' },
  { id: 'prop65',  label: 'Prop 65',            desc: 'NSRL / MADL warning determination' },
  { id: 'sds',     label: 'SDS',                desc: 'OSHA / GHS 16-section Safety Data Sheet' },
  { id: 'claims',  label: 'Claims Support',     desc: 'Pregnancy / kids / sensitive substantiation' },
];

export default function AssessorRunPanel() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);

  const [productName, setProductName] = useState('Assessor Test Formula');
  const [mode, setMode] = useState('hazard');
  const [clientUserId, setClientUserId] = useState('');  // '' = scratchpad
  const [productCategory, setProductCategory] = useState('face_cream');
  const [rows, setRows] = useState([
    { inci_name: 'Glycerin', concentration: 5 },
    { inci_name: 'Phenoxyethanol', concentration: 1 },
  ]);
  const [notes, setNotes] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  // Bootstrap clients + product-category list
  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          axios.get(`${API_URL}/api/admin/clients`, { withCredentials: true }),
          axios.get(`${API_URL}/api/admin/exposure/scenarios`, { withCredentials: true }),
        ]);
        setClients(c.data.clients || []);
        setCategories(s.data.scenarios || []);
      } catch (e) {
        // non-fatal — page still works without these prefilled
      }
    })();
  }, []);

  const groupedCategories = useMemo(() => {
    const g = {};
    for (const s of categories) {
      const k = s.group || 'Other';
      if (!g[k]) g[k] = [];
      g[k].push(s);
    }
    return g;
  }, [categories]);

  const addRow = () => setRows(rs => [...rs, { inci_name: '', concentration: 0 }]);
  const setRow = (i, patch) => setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const removeRow = (i) => setRows(rs => rs.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(''); setSuccess(null);
    const valid = rows
      .filter(r => r.inci_name?.trim() && parseFloat(r.concentration) > 0)
      .map(r => ({
        inci_name: r.inci_name.trim(),
        concentration: parseFloat(r.concentration),
      }));
    if (!productName.trim()) { setError('Product name is required.'); return; }
    if (valid.length === 0) { setError('At least one ingredient with concentration > 0 is required.'); return; }

    setSubmitting(true);
    try {
      const r = await axios.post(`${API_URL}/api/formulations`, {
        metadata: {
          product_name: productName.trim(),
          product_type: 'leave-on',
          target_population: 'adult',
          application_area: 'face',
          frequency_of_use: 2,
          product_category: productCategory,
          safety_claims: [],
        },
        ingredients: valid,
        assessment_mode: mode,
        client_user_id: clientUserId || null,
      }, { withCredentials: true });
      setSuccess({
        id: r.data.id || r.data._id,
        product_name: productName.trim(),
        client_name: clientUserId ? (clients.find(c => c.id === clientUserId)?.name || '—') : 'Assessor scratchpad',
        overall_status: r.data.overall_status,
      });
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to run assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setSuccess(null);
    setRows([{ inci_name: '', concentration: 0 }]);
    setProductName('Assessor Test Formula');
    setNotes('');
  };

  return (
    <div className="card-flat p-5 bg-white" data-testid="assessor-run-panel">
      <div className="flex items-center gap-2 mb-1">
        <Flask size={18} className="text-[#FF4E6A]" weight="fill" />
        <h2 className="text-sm font-bold text-[#111827]">Run Assessment</h2>
        <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#4B5563] bg-[#F3F4F6] px-2 py-0.5 rounded ml-auto">scratchpad · proxy</span>
      </div>
      <p className="text-[11px] text-[#4B5563] mb-4">
        Run any of the six workflows internally, or attribute the result to a brand client (the formulation will appear in their dashboard with an <em>assessor proxy</em> tag).
      </p>

      {/* Workflow picker */}
      <div className="mb-3">
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Workflow</Label>
        <div className="mt-1.5 grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {MODES.map(m => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={`text-left px-2.5 py-2 rounded border text-[11px] transition-colors ${mode === m.id ? 'border-[#FF4E6A] bg-[#FFE9EE]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
              data-testid={`run-mode-${m.id}`}
            >
              <p className="font-bold text-[#111827]">{m.label}</p>
              <p className="text-[10px] text-[#4B5563] mt-0.5">{m.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Client + product meta */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Run as</Label>
          <select
            value={clientUserId}
            onChange={(e) => setClientUserId(e.target.value)}
            className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white"
            data-testid="run-client-picker"
          >
            <option value="">Assessor scratchpad (own account)</option>
            {clients.map(c => (
              <option key={c.id} value={c.id}>{c.name || c.email} {c.company ? `· ${c.company}` : ''}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Product name</Label>
          <Input
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            className="mt-1 h-9 text-sm"
            placeholder="e.g. Vitamin C Serum 10%"
            data-testid="run-product-name"
          />
        </div>
      </div>

      <div className="mb-3">
        <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Product category</Label>
        <select
          value={productCategory}
          onChange={(e) => setProductCategory(e.target.value)}
          className="mt-1 w-full h-9 text-sm border border-gray-300 rounded px-2 bg-white"
          data-testid="run-product-category"
        >
          {Object.entries(groupedCategories).map(([g, items]) => (
            <optgroup key={g} label={g}>
              {items.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </optgroup>
          ))}
          {categories.length === 0 && <option value="custom">Custom / Other</option>}
        </select>
      </div>

      {/* Ingredients */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1.5">
          <Label className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#4B5563]">Ingredients</Label>
          <Button variant="outline" onClick={addRow} className="h-7 text-xs rounded" data-testid="run-add-ingredient"><Plus size={12} className="mr-1" weight="bold" /> Add</Button>
        </div>
        <div className="space-y-1.5">
          {rows.map((row, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input
                placeholder="INCI name"
                value={row.inci_name}
                onChange={(e) => setRow(i, { inci_name: e.target.value })}
                className="h-8 text-sm flex-1"
                data-testid={`run-inci-${i}`}
              />
              <Input
                type="number" step="0.01"
                placeholder="0.0"
                value={row.concentration}
                onChange={(e) => setRow(i, { concentration: e.target.value })}
                className="h-8 text-sm w-24 text-right tabular-nums"
                data-testid={`run-conc-${i}`}
              />
              <span className="text-[10px] text-[#6B7280] w-4">%</span>
              <button
                type="button"
                onClick={() => removeRow(i)}
                className="text-[#9B1C1C] hover:text-[#7B1313]"
                data-testid={`run-remove-${i}`}
              ><Trash size={14} /></button>
            </div>
          ))}
        </div>
      </div>

      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Optional internal notes (not saved to the formulation yet)"
        className="text-xs mb-3"
        rows={2}
        data-testid="run-notes"
      />

      {error && (
        <div className="text-[11px] text-[#9B1C1C] bg-[#FDE8E8] border border-[#F8B4B4] rounded p-2 mb-2 flex items-start gap-1.5" data-testid="run-error">
          <Warning size={14} weight="fill" className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {success ? (
        <div className="border border-[#31C48D] bg-[#DEF7EC] rounded p-3 text-[12px] text-[#03543F]" data-testid="run-success">
          <div className="flex items-start gap-2">
            <CheckCircle size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold">Assessment created · {success.overall_status?.replace('_', ' ')}</p>
              <p className="text-[11px] mt-0.5">{success.product_name} · attributed to <strong>{success.client_name}</strong></p>
            </div>
          </div>
          <div className="flex gap-2 mt-2">
            <Button onClick={() => navigate(`/assessment/${success.id}`)} className="h-7 text-xs rounded bg-[#0B1426] hover:bg-[#1B2538] text-white" data-testid="run-go-to-assessment">
              Open assessment <ArrowRight size={11} className="ml-1" weight="bold" />
            </Button>
            <Button onClick={reset} variant="outline" className="h-7 text-xs rounded" data-testid="run-reset">Run another</Button>
          </div>
        </div>
      ) : (
        <Button
          onClick={submit}
          disabled={submitting}
          className="w-full h-9 rounded bg-[#FF4E6A] hover:bg-[#E63E5A] text-white text-sm font-semibold"
          data-testid="run-submit"
        >
          {submitting ? 'Running…' : `Run ${MODES.find(m => m.id === mode)?.label}`}
        </Button>
      )}
    </div>
  );
}
